import React from 'react'
import {
	DndContext,
	closestCenter,
	PointerSensor,
	useSensor,
	useSensors,
} from '@dnd-kit/core'
import {
	arrayMove,
	SortableContext,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { AnimatePresence } from 'framer-motion'
import { ColumnRow, Column } from './ColumnRow'
import { useTranslation } from '@/hooks/useTranslation'

interface ColumnManagerProps {
	columns: Column[]
	onColumnsChange: (columns: Column[]) => void
	isSaving: boolean
}

export function ColumnManager({
	columns,
	onColumnsChange,
	isSaving,
}: ColumnManagerProps) {
	const { t } = useTranslation()
	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 8,
			},
		})
	)

	const handleDragEnd = (event: any) => {
		const { active, over } = event
		if (active.id !== over.id) {
			const oldIndex = columns.findIndex((item) => item.id === active.id)
			const newIndex = columns.findIndex((item) => item.id === over.id)
			const reordered = arrayMove(columns, oldIndex, newIndex)
			onColumnsChange(
				reordered.map((col, index) => ({ ...col, order_index: index }))
			)
		}
	}

	const updateColumn = (id: string, updates: Partial<Column>) => {
		onColumnsChange(
			columns.map((col) => (col.id === id ? { ...col, ...updates } : col))
		)
	}

	const removeColumn = (id: string) => {
		const filtered = columns.filter((col) => col.id !== id)
		onColumnsChange(
			filtered.map((col, index) => ({ ...col, order_index: index }))
		)
	}

	return (
		<div className="space-y-4">
			{columns.length > 0 && (
				<>
					{/* Desktop Headers - Hidden on mobile */}
					<div className="hidden md:grid md:grid-cols-12 md:items-center md:gap-4 px-4 py-3 bg-muted/30 border border-border/50 rounded-lg">
						<div className="col-span-1"></div>
						<div className="col-span-3">
							<h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
								{t('columnManager.columnName')}
							</h4>
						</div>
						<div className="col-span-3">
							<h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
								{t('columnManager.mapsToStatus')}
							</h4>
						</div>
						<div className="col-span-4">
							<h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
								{t('columnManager.color')}
							</h4>
						</div>
						<div className="col-span-1 text-center">
							<h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
								{t('columnManager.actions')}
							</h4>
						</div>
					</div>

					{/* Sortable Column Cards */}
					<DndContext
						sensors={sensors}
						collisionDetection={closestCenter}
						onDragEnd={handleDragEnd}>
						<SortableContext
							items={columns}
							strategy={verticalListSortingStrategy}>
							<div className="space-y-3">
								<AnimatePresence>
									{columns.map((column) => (
										<ColumnRow
											key={column.id}
											column={column}
											onUpdate={updateColumn}
											onRemove={removeColumn}
											isSaving={isSaving}
										/>
									))}
								</AnimatePresence>
							</div>
						</SortableContext>
					</DndContext>
				</>
			)}

			{columns.length === 0 && (
				<div className="text-center py-12 px-4 text-muted-foreground border-2 border-dashed border-muted rounded-lg bg-muted/20">
					<div className="max-w-sm mx-auto space-y-3">
						<div className="w-16 h-16 mx-auto bg-muted/50 rounded-full flex items-center justify-center">
							<svg
								className="w-8 h-8"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24">
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={1.5}
									d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
								/>
							</svg>
						</div>
						<div>
							<p className="text-sm font-medium">{t('columnManager.noColumnsConfiguredYet')}</p>
							<p className="text-xs mt-1 text-muted-foreground/70">
								{t('columnManager.clickAddColumnToCreate')}
							</p>
						</div>
					</div>
				</div>
			)}
		</div>
	)
}

// Re-export the Column interface for convenience
export type { Column } from './ColumnRow'
